import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRegistrationContext } from "../../../contexts/ClientRegistrationContext";
import { uploadUserAgreement } from "../../../services/additionalFormService";

// On submission, form will be 'signed'
// Continue to next page and when building client, store link to auth form in Client -> AdditionalForms
const UserAgreement = () => {
  const { data, updateData } = useRegistrationContext();
  const [isAgreed, setIsAgreed] = useState(data.userAgreement || false);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const location = useLocation();
  let clientID = location.state?.clientID;
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const DEV_BLOB_URL = process.env.REACT_APP_DEV_BLOB_STORAGE_URL || "";

  const BLOB_URL = DEV_BLOB_URL;

  const pdfUrl = `${BLOB_URL}/documents/User-Agreement.pdf`;

  const handleContinue = async () => {
    if (isAgreed) {
      setLoadingSpinner(true);
      if (clientID != null) {
        await uploadUserAgreement(clientID);
        navigate("/client/current-orders");
        return;
      }
      updateData({ userAgreement: true });
      navigate("/client/registration/key-information");
    } else {
      setErrorMessage("Please agree to the User Agreement before continuing.");
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Step 1: User Agreement</h1>
      </div>
      <div className="error">
        You must complete the user agreement before you can access your portal.
      </div>
      <div className="divider"></div>
      {/* User Agreement Client Form */}
      <div className="text-header">Background Investigative Reports</div>
      <div className="text-container-scroll">
        <div className="text-body">
          <b>Last Updated: May 1, 2025</b>
          <br></br>
          <div>
            Welcome to Oxford Document Management. Oxford Document Management,
            LLC (“Oxford”, “we”, or similar) offers candidate screening services
            through our online platform, as well as in an offline professional
            services capacity (collectively, the “Services”). “Client,” “you,”
            or similar means the organization, business, or entity engaging us
            to provide the Services.
          </div>
          <br></br>
          <div>
            This Client Agreement (“Agreement”) is by and between you and Oxford
            and governs your access and use of our Services. By: (i) accessing
            or otherwise interacting with our software platform, or (ii)
            otherwise clicking “I Agree,” “Submit,” “Create Account,” or similar
            button or check box when prompted, you accept and agree to be bound
            and abide by this Agreement. Each Client acknowledges and agrees
            that Oxford may rely upon the individual who interacts with the
            Services online as having the authority to enter into and bind
            Client to this Agreement; Client is not permitted to later revoke
            its assent to the terms herein.
          </div>
          <br></br>
          <div>
            You and Oxford further agree that the terms and conditions of this
            Agreement as of the “Last Updated” date above hereby supersede and
            replace any prior agreements between you and Oxford, even if the
            form of this Agreement is inconsistent with the prior agreement’s
            requirements for modification or amendments.
          </div>
          <br></br>
          <div>
            We further reserve the right to change or amend this Agreement from
            time to time. Except as otherwise required by law, all changes shall
            be immediately effective upon their posting to the Services. You
            acknowledge and agree that your continued access and use of the
            Services following such changes shall be deemed acceptance by you of
            any new or amended provisions of this Agreement.
          </div>
          <ol>
            <li>
              <b>SERVICES</b>. Oxford will provide you the Services according to
              the scope and duration of Services agreed upon by the parties,
              which will include the coordinating, gathering, and disseminating
              of background investigative consumer reports, which may include
              information related to an individual’s employment, references,
              education, professional license, credit, driving, and criminal
              histories obtained through third-party systems, interviews, or
              other sources (collectively, the “Reports”). The scope and content
              of the Report depends on your instructions and requirements for
              the Report communicated to Oxford in writing or via your
              configuration of position(s) within the Oxford online portal (the
              “Platform”). For clarity, Oxford has no duty to commence or
              perform any independent investigation beyond the ordering and
              collection of Reports consistent with Oxford’s procedures. If
              Client requires candidates to execute any documentation via a
              notary witness, Client is solely responsible for obtaining the
              original copy; Oxford only stores or processes copies of such
              documents (whether digital or otherwise). Client represents and
              warrants it has otherwise inspected the Services and the Services
              are acceptable to Client for its purposes, including Oxford’s use
              of electronic means to obtain candidates’ consent.
            </li>
            <li>
              <b>REPORTS</b>. Client understands and agrees that it is solely
              responsible and liable for determining the scope and content of
              the Reports requested from Oxford, the interpretation, use, and
              release of any and all information from the Reports, for complying
              with all laws regarding the scope, content, use, or release of any
              and all information from the Report, and for any and all actions
              taken based on or related to the Report or information from it.
              Client understands that Seller is not providing legal advice
              regarding the scope, content, use, release, or any other matter
              related to the Reports or any information contained therein, and
              that Client should obtain whatever legal advice it determines is
              needed prior to entering this Agreement, prior to making any
              employment or volunteer service related decision in whole or in
              part on the information obtained from the Reports, prior to making
              any other use or release of the Report or any information
              contained therein, or for any other matters related to the Report.
              Client represents and warrants that it has and will maintain, and
              that the Report and all information from it will be used only for,
              legitimate permissible purposes in compliance with all applicable
              state and federal laws including but not limited to the Fair
              Credit Reporting Act, including sections 604(b) and 607(d), and
              the Americans with Disabilities Act. Client becomes the owner of
              all Reports submitted to it and may use, file, or dispose of them
              in any manner in compliance with applicable laws.
            </li>
            <li>
              <b>CONFIDENTIAL REPORTING</b>. In the event that any evidence of
              or information relating to sexual misconduct, criminal activity,
              or other high-risk events or conduct is obtained by Oxford, Oxford
              will report such information directly and confidentially to the
              individual designated by Client to receive such information
              (“Client Representative”). If no such person has been identified
              by Client in writing, then the Client Representative will be
              deemed to be Oxford’s primary Client contact.
            </li>
            <li>
              <b>RETENTION</b>. The documentation associated with each Report
              generated by Oxford (the “Background Files”) shall be maintained
              and stored by Oxford at its expense for a period of at least five
              (5) years from the date the background investigation is commenced,
              unless this Agreement is earlier terminated or Oxford is otherwise
              instructed by Client in writing. At the conclusion of the holding
              period and/or upon termination of this Agreement (or earlier if
              required by applicable laws), with respect to any Background Files
              in Oxford’s possession, Oxford shall follow any written
              instructions from Client with respect to disposition of the files,
              or, in the absence of such written instruction, shall forward all
              Background Files in its possession to the Client Representative at
              Client's expense.
            </li>
            <li>
              <b>TERM</b>.
              <ol style={{ listStyle: "lower-roman" }}>
                <li>
                  Term. This Agreement will commence on the date Client first
                  accesses or uses the Service following posting, or the date
                  Client indicates their acceptance of this Agreement, or
                  another date agreed upon by the parties in writing, whichever
                  occurs first, and shall continue for a period of one (1) year.
                  This Agreement shall then automatically renew for additional
                  one-year periods unless either party gives notice to the other
                  party of its intent to not renew the Agreement at least thirty
                  (30) days prior to the end of the then-current term. Client
                  shall deliver its notice to Oxford at
                  amunyon@oxforddoc.com.{" "}
                </li>
                <li>
                  Termination. This Agreement may be terminated by either party
                  upon the other party’s material breach of the terms herein by
                  providing notice of the same and a thirty (30) day opportunity
                  for the breaching party to cure the breach. Clients are not
                  entitled to any refund of fees. Oxford is under no further
                  obligations to you upon your termination of this Agreement.
                </li>
              </ol>
              <div>
                Oxford further has the right to terminate your ability to use
                the Services immediately for security reasons, or in the event
                Oxford believes that you have used the Services in violation of
                applicable laws or this Agreement.{" "}
              </div>
            </li>
            <li>
              <b>FEES</b>. Use of the Services is subject to Oxford’s current
              Fee Schedule. Client shall pay for each Report based on Oxford’s
              current Fee Schedule. Oxford reserves the right to change the Fee
              Schedule at any time, with or without notice. Oxford may charge a
              one-time setup fee of $50.00 for new customers, billed only when
              services are first rendered. Client will be billed monthly in
              arrears via invoice. All invoices are due upon receipt. Payments
              not received within ninety (90) days of the date of invoice will
              be considered past due and may result in suspension of the
              Services. Past due amounts may accrue interest at the rate of 1.5%
              per month or the maximum amount permitted by law, whichever is
              less. Client shall reimburse Oxford for all expenses, fees, and
              costs incurred by Oxford in recouping any past due or overdue
              amounts or interest from Client.
            </li>
            <li>
              <b>OXFORD INTELLECTUAL PROPERTY</b>. You acknowledge that, as
              between you and Oxford, Oxford owns all right, title, and
              interest, including all intellectual property rights, in and to
              the Services (excluding the Reports and Client’s Confidential
              Information), including all software, hardware, firmware, and
              derivatives of the same, and, with respect to third-party data or
              information, the applicable third-party owns all right, title, and
              interest, including all intellectual property rights, in and to
              the third-party data, name, image, and likeness, and trademarks.
              Oxford’s names and logos and all related product and service
              names, design marks, and slogans, including the OXFORD name and
              logo as displayed on our website, are the trademarks or service
              marks of Oxford. All rights are reserved. You are not authorized
              to use any Oxford name or mark in any advertisement, publicity or
              in any other commercial manner without prior written consent of
              Oxford. All other trademarks, trade dress, images, photographs,
              and other works appearing in connection with the Services are the
              property of their respective owners. The entire contents and
              design of the Services are protected by U.S. and international
              copyright law. All rights regarding the Services and materials
              contained on or in the Services, including any and all technology,
              code, or design related to the Services, are either owned by
              Oxford, are licensed to it, or are used with permission. Oxford
              and its licensors, vendors, or other service providers retain and
              reserve all intellectual and proprietary rights to the Services.
              You are expected to obey all copyright restrictions. Copying or
              storing of any content without the prior written permission from
              Oxford or the intellectual property holder is prohibited. Please
              use the contact information below if you desire such permission or
              need more information.
            </li>
            <li>
              <b>PROHIBITED USES AND CONDUCT</b>. You may use the Services only
              for lawful purposes and in accordance with this Agreement. You
              agree not to use the Services: (i) in any way that violates any
              applicable federal, state, local, or international law or
              regulation (including, without limitation, any laws regarding the
              export of data or software to and from the U.S. or other
              countries); (ii) for the purpose of exploiting, harming, or
              attempting to exploit or harm any person in any way by exposing
              them to inappropriate content, facilitating harassment, or
              otherwise; (iii) to impersonate or attempt to impersonate Oxford,
              an Oxford employee, another user, or any other person or entity;
              or (iv) to engage in any other conduct that restricts or inhibits
              anyone's use or enjoyment of the Services, or which, as determined
              by us, may harm Oxford or users of the Services, or expose them to
              liability. You agree not to, and will not permit any third party
              to, attempt to gain unauthorized access to, interfere with,
              damage, or disrupt any parts of the Platform, any Oxford accounts,
              the servers on which the Platform is hosted or stored, or any
              server, computer, information, or database connected to the
              Platform. You are solely responsible for any liability associated
              with your use of the Services.
            </li>
            <li>
              <b>PLATFORM PERMISSIONS</b>. Subject to your compliance with this
              Agreement, we hereby grant you a limited, non-exclusive,
              non-transferable right to access and use our Platform during the
              Term, solely and strictly in accordance with this Agreement for
              lawful purposes. You understand and agree that the Platform is
              provided under license to you, not being sold to you, and you do
              not gain any ownership interest of any kind in the Platform under
              this Agreement. You are not in any way authorized by the license
              granted hereunder, or this Agreement, to exploit the Platform to
              compete with the business of Oxford. In order to access the
              Platform, you may be required to create an account with Oxford
              that may include choosing an account name and setting a password.
              When you do so, the information you provide Oxford must be
              accurate, complete and up-to-date. When registering with Oxford,
              you shall not impersonate anyone else, choose names that are
              offensive, or violate anyone’s rights. If you do not follow these
              rules, we may cancel your account. Depending on the level of
              permissions assigned to your user account, you may not have access
              to or be able to view or use all of the functions or features of
              the Platform.
            </li>
            <li>
              <b>ACCOUNT SECURITY</b>. You acknowledge and agree that you are
              responsible for all the activity on your account, and for keeping
              your account credentials secure and confidential. If you have
              reason to believe that someone has accessed or used your account
              without your permission, you must immediately change your password
              and report the incident to Oxford using the “Contact Us” details
              below. You will be held liable for losses incurred by Oxford, or
              any other user of the Platform, due to someone else using your
              Oxford password or account. You shall not use anyone else’s Oxford
              password or account at any time. Oxford cannot and will not be
              liable for any loss or damage arising from your failure to comply
              with these obligations.
            </li>
            <li>
              <b>FEEDBACK</b>. Oxford may solicit your feedback regarding your
              experience accessing or using the Services. The comments,
              information, and details you submit to us through surveys,
              reviews, or other forums regarding your experience accessing or
              using the Services, including without limitation any suggestions
              or recommendations regarding the Services’ features or
              functionality, including any improvements (“Feedback”) may be used
              by Oxford without any obligation, limitation, attribution, or
              compensation to any party. You hereby assign to Oxford all right,
              title, and interest, including intellectual property rights, in
              and to any ideas, inventions, know-how, concepts, techniques, or
              other concepts contained in the Feedback. For clarity, Feedback
              does not include Client Content.
            </li>
            <li>
              <b>PAYMENT METHOD</b>. You may be asked to supply certain relevant
              information, including your credit or payment card number and
              expiration date or similar payment information, your billing
              address, and email. You represent and warrant that you have the
              right to use any credit or payment card or other payment mechanism
              that you submit in connection with the purchase of a subscription
              and that you have all authority necessary to use the credit card
              or payment mechanism that you submit. By submitting such
              information, you grant Oxford the right to provide such
              information to third parties for the purposes of facilitating your
              payment. You acknowledge and agree that Oxford is not responsible
              for how any third-party credit card or other payment method
              processor transmits, stores, uses or shares your information.
            </li>
            <li>
              <b>FUNCTIONALITY AND UPDATES</b>. Oxford reserves the right to
              change, suspend, or discontinue any aspect of the Services at any
              time, including availability of a feature, database, or content.
              We may from time to time in our sole discretion develop and
              provide Services updates, which may include upgrades, bug fixes,
              patches and other error corrections and/or new features
              (collectively, including related documentation, “Updates”).
              Updates may also modify or delete in their entirety certain
              features and functionality. You agree that we have no obligation
              to provide any Updates or to continue to provide or enable any
              particular features or functionality.
            </li>
            <li>
              <b>THIRD-PARTY SITES AND LINKS</b>. Oxford makes reasonable
              efforts to determine that other website owned or operated by third
              parties accessible via the Services (collectively, “Third-Party
              Websites”) are active and appropriate at the time the links to
              such Third-Party Websites are placed in the Services. Oxford has
              no control over, and assumes no liability for, the continued
              existence of, or the material available on, any Third-Party
              Website. Should you discover that any link in the Services
              references a Third-Party Website that is no longer active or that
              contains inappropriate or irrelevant information, please notify us
              by sending an e-mail message to the address listed below. Links
              from the Services to Third-Party Websites do not constitute or
              imply an endorsement, sponsorship, or recommendation by Oxford or
              any of its subsidiaries or affiliates of the third party, the
              Third-Party Website, or the resources, information, or content
              contained therein. Oxford does not endorse or warrant, and is not
              responsible or liable for, any such Third-Party Website or the
              content thereon. Please review the applicable third party’s terms
              and conditions and privacy policy when interacting with or using
              any Third-Party Website.
            </li>
            <li>
              <b>CONSENT TO DOING BUSINESS ELECTRONICALLY</b>. You hereby agree
              that we may deliver all notices, disclosures, documents, reviews,
              analyses, or other materials, and any and all other documents,
              information, and communication concerning you or Oxford by means
              of email or by posting in the Services or by other means of
              electronic communication. Considering Oxford operates principally
              on the internet, by accessing the Services, you hereby consent to
              transact business electronically and to receive electronically all
              disclosures, documents, communications, notices, contracts, and
              agreements arising from or relating in any way to your or our
              rights, obligations, or services under this Agreement or any other
              agreement between you and Oxford.
            </li>
            <li>
              <b>INDEMNIFICATION</b>. You agree to defend, indemnify and hold
              harmless Oxford and its affiliates, and each of their directors,
              officers, employees, and agents from and against all losses,
              expenses, damages, and costs, including reasonable attorneys’
              fees, resulting from your breach or violation of any
              representations, warranties, covenants, obligations, or other
              provisions set forth in this Agreement or your breach of
              applicable laws in connection with your use of the Services. We
              reserve the right, but not the obligation, to take over the
              exclusive defense and control of any claim for which we are
              entitled to indemnification under this Agreement. In such event,
              you hereby agree to cooperate and help us in asserting any
              defenses to such claims.
            </li>
            <li>
              <b>DISCLAIMERS</b>. Oxford shall use good faith in attempting to
              obtain information from sources deemed reliable, but Client
              recognizes that information in the Reports is secured from and
              managed by fallible sources (human and otherwise), and that for
              the fee charged, Oxford cannot be either an insurer or guarantor
              of the accuracy of the information reported. Oxford disclaims any
              and all liability for the acts, omission, and conduct of Client or
              third parties in connection with or related to Client’s use of the
              Services. Client is completely responsible for its use of the
              Services. Client’s sole remedy against Oxford for dissatisfaction
              with the Services or any content is to stop using the Services
              and/or reperform the Services at issue. This limitation of relief
              is a part of the bargain between the parties. THE SERVICES MAY
              CONTAIN TECHNICAL ERRORS, TYPOGRAPHICAL MISTAKES, OR OTHER
              INACCURACIES. UNLESS OTHERWISE EXPRESSED, THE SERVICES ARE
              PROVIDED “AS-IS,” WITHOUT ANY WARRANTY AND OXFORD EXPRESSLY
              DISCLAIMS ALL WARRANTIES AND CONDITIONS OF ANY KIND, WHETHER
              EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, ANY IMPLIED
              WARRANTIES AND CONDITIONS OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE OR NON-INFRINGEMENT, OR ANY WARRANTY THAT THE
              PLATFORM IS ACCURATE, ADEQUATE, OR FREE OF VIRUSES OR OTHER
              HARMFUL COMPONENTS.
            </li>
            <li>
              <b>LIMITATION OF LIABILITY</b>. IN NO EVENT WILL OXFORD OR ITS
              AFFILIATES BE LIABLE UNDER OR IN CONNECTION WITH THIS AGREEMENT
              UNDER ANY LEGAL OR EQUITABLE THEORY, INCLUDING BREACH OF CONTRACT,
              TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, AND OTHERWISE, FOR
              ANY: (i) CONSEQUENTIAL, INCIDENTAL, INDIRECT, EXEMPLARY, SPECIAL,
              ENHANCED, OR PUNITIVE DAMAGES; (ii) INCREASED COSTS, DIMINUTION IN
              VALUE OR LOST BUSINESS, PRODUCTION, REVENUES, OR PROFITS; (iii)
              LOSS OF GOODWILL OR REPUTATION; (iv) USE, INABILITY TO USE, LOSS,
              INTERRUPTION, DELAY OR RECOVERY OF ANY DATA, OR BREACH OF DATA OR
              SYSTEM SECURITY; (v) COST OF REPLACEMENT GOODS OR SERVICES; OR
              (vi) DAMAGE OR INJURY TO PERSON OR PROPERTY, IN EACH CASE
              REGARDLESS OF WHETHER OXFORD WAS ADVISED OF THE POSSIBILITY OF
              SUCH LOSSES OR DAMAGES OR SUCH LOSSES OR DAMAGES WERE OTHERWISE
              FORESEEABLE. IN NO EVENT WILL OXFORD’S OR ITS AFFILIATES’
              AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THIS AGREEMENT OR
              YOUR USE OF THE SERVICES UNDER ANY LEGAL OR EQUITABLE THEORY,
              INCLUDING BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT
              LIABILITY, AND OTHERWISE EXCEED THE TOTAL FEES PAID BY YOU TO
              OXFORD IN THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO
              THE CLAIM.
            </li>
            <li>
              <b>CONFIDENTIAL INFORMATION</b>. “Confidential Information” as
              used in this Agreement means any and all non-public information
              disclosed by one party or its agents or representatives
              (“Disclosing Party”) to the other party or its agents or
              representatives (“Receiving Party”) whether intentionally or
              unintentionally, or otherwise viewed, heard, accessed, or obtained
              in any way, during the Term. “Confidential Information” will not
              include any information (a) in the public domain at or subsequent
              to the time it was communicated to the Receiving Party through no
              fault of the Receiving Party; or (b) rightfully in the Receiving
              Party’s possession free of any obligation of confidence at or
              subsequent to the time it was communicated to the Receiving Party.
              The Receiving Party will not disseminate, or in any way disclose,
              publish, reveal, report or transfer, directly or indirectly,
              Confidential Information to any person, organization, entity, or
              business except as expressly provided herein. In no event shall
              the Receiving Party use, or permit any third party to use, the
              Confidential Information for its own personal benefit, or to
              compete with or disadvantage the Disclosing Party in any way. The
              Receiving Party will use Confidential Information solely for
              performing its rights or obligations under this Agreement. Each
              party will treat all Confidential Information of the other party
              with the same degree of care as it accords to its own Confidential
              Information, and each party represents that it exercises
              reasonable care at all times to protect its own Confidential
              Information. The Receiving Party shall be responsible for the
              conduct of its personnel (including vendors, subcontractors, or
              service providers), directors, officers, subsidiaries, affiliates,
              employees and representatives regarding the confidentiality and
              use of the Confidential Information. The Receiving Party shall
              only disclose the Confidential Information to personnel,
              investors, and representatives that are bound by confidentiality
              obligations that are at least as restrictive as the terms of this
              Agreement. The Receiving Party will immediately give notice to the
              Disclosing Party of the date of, and circumstances involved in,
              any loss of or unauthorized use or disclosure of any Confidential
              Information. The Receiving Party will assist the Disclosing Party
              in remedying any such unauthorized use or disclosure of the
              Confidential Information, including by securing the return of the
              Confidential Information from the party to whom the Confidential
              Information was disclosed. In the event that the Receiving Party
              is required by law, regulation or court order to disclose any of
              the Disclosing Party’s Confidential Information, the Receiving
              Party will promptly notify the Disclosing Party in writing prior
              to making any such disclosure in order to facilitate the
              Disclosing Party’s seeking a protective order or other appropriate
              remedy from the proper authority. Due to the unique confidential,
              proprietary, unique and valuable nature of the Confidential
              Information, the Receiving Party acknowledges and agrees that in
              the event the Receiving Party fails to comply with its obligations
              hereunder, that monetary damages may be inadequate to compensate
              the Disclosing Party. Accordingly, the Receiving Party agrees that
              the Disclosing Party shall, in addition to any other remedies
              available to it at law or in equity, be entitled to seek
              injunctive relief to enforce the terms of this Agreement.
            </li>
            <li>
              <b>GOVERNING LAW</b>. This Agreement shall be construed and
              governed in accordance with the laws of the State of Minnesota,
              without giving effect to any choice or any conflicts of law
              provisions or rules. Any action relating to this Agreement shall
              be brought in the federal or state courts located in the City of
              Minneapolis, State of Minnesota. You hereby agree to personal
              jurisdiction and submit to venue by such courts, and waive any
              jurisdictional, venue, or inconvenient forum objections to such
              courts. The parties acknowledge and agree that Oxford is
              established in the United States and, as a result, the Services
              are designed to comply with U.S. laws. This Agreement shall not be
              construed as causing Oxford to be subject to laws outside of the
              United States, and Client agrees that it is solely responsible for
              its compliance with local laws and regulations.
            </li>
            <li>
              <b>CLASS ACTION WAIVER; WAIVER OF JURY TRIAL</b>. THE PARTIES
              AGREE THAT ANY CLAIMS WILL BE ADJUDICATED ON AN INDIVIDUAL BASIS,
              AND EACH WAIVES THE RIGHT TO PARTICIPATE IN A CLASS, COLLECTIVE,
              PAGA, OR OTHER JOINT ACTION WITH RESPECT TO THE CLAIMS. EACH OF
              THE PARTIES HERETO HEREBY IRREVOCABLY WAIVES ANY AND ALL RIGHT TO
              TRIAL BY JURY IN ANY LEGAL PROCEEDING ARISING OUT OF OR RELATED TO
              THIS AGREEMENT OR THE TRANSACTIONS CONTEMPLATED HEREBY.
            </li>
            <li>
              <b>MISCELLANEOUS</b>. This Agreement constitutes the sole and
              entire agreement between you and Oxford regarding the Services and
              supersedes all prior and contemporaneous understandings,
              agreements, representations and warranties, both written and oral,
              regarding the Services. It is expressly agreed that Oxford and all
              Clients shall be independent contractors of each other and that
              the relationship between the parties shall not constitute a
              partnership, joint venture or agency. Neither Oxford nor any
              Client shall have the authority to make any statements,
              representations or commitments of any kind, or to take any action,
              which shall be binding on the other party, without the prior
              consent of such other party. In addition to this Agreement, you
              may enter into other agreements with us that will govern your use
              of the Services. If there is any contradiction or conflict between
              this Agreement and another agreement you enter into with us
              applicable to the Services, the other agreement shall take
              precedence in relation to the specific aspects of the Services
              governed by such agreement. If any part, term, or provision of
              this Agreement is held to be illegal, in conflict with any law, or
              otherwise invalid, the remaining portion or portions shall be
              considered severable and not be affected, and the rights and
              obligations of the parties shall be construed and enforced as if
              this Agreement did not contain the particular part, term, or
              provision held to be illegal or invalid. The provisions of this
              Agreement shall survive any expiration or termination of this
              Agreement or completion of any services hereunder to the extent
              necessary or appropriate to carry out the intent of such
              provisions. We may assign our rights and obligations under this
              Agreement, including in connection with a merger, acquisition,
              sale of assets or equity, or by operation of law. You may not
              assign rights and obligations under this Agreement, including in
              connection with a merger, acquisition, sale of assets or equity,
              or by operation of law, without our prior written consent. The
              failure by us to enforce any right or provision of this Agreement
              will not prevent us from enforcing such right or provision in the
              future.
            </li>
            <li>
              <b>CONTACT US</b>. If you have questions regarding this Agreement
              or the Services, please contact us by e-mail at{" "}
              <a href="amunyon@oxforddoc.com">amunyon@oxforddoc.com </a>
              or PO Box 307, Anoka MN 55303.
            </li>
          </ol>
        </div>
      </div>
      <br></br>
      <div className="check-box">
        <input
          className="form-check-input"
          type="checkbox"
          value=""
          id="flexCheckDefault"
          checked={isAgreed}
          onChange={() => {
            setIsAgreed(!isAgreed);
          }}
          disabled={data.userAgreement}
        />
        <label className="form-check-label">
          <b>
            I have read and hereby agree to be bound by the terms and conditions
            of the User Agreement.
          </b>
        </label>
      </div>
      <br></br>
      <div className="text-body">
        For a printable version of the "User Agreement",{" "}
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
          click here
        </a>
        . A new tab will open up. Print that page and then close that window to
        return to this page.
      </div>
      <br></br>
      <button className="oxbtn btn-purple" onClick={handleContinue}>
        Continue
      </button>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default UserAgreement;
